// src/pages/LookUp/ActsCareCounseling/Needs/CustomForms/NeedCustomField.tsx
/**
 * Per-need custom intake fields.
 *
 * A `CareNeed` can declare extra questions the public help-request form asks, exactly as a course can
 * for registrations. This is the counselling mirror of
 * `Grow/Courses/[CourseDetails]/CustomForms/CourseCustomField.tsx`, and it reuses the same
 * `FormBuilder` + `createCustomFieldAdapter` machinery unchanged.
 *
 * ⚠️ **The adapter needs no need-specific code.** Its primary-key detection is `/CustomFieldID$/i`,
 * which already matches `needCustomFieldID`, and `NeedCustomFieldModelView` is field-for-field
 * identical to the course shape. If that ever stops being true, fix it in the adapter, not here.
 *
 * ⚠️ **There is no `useNeedCustomFieldService()` hook** in the SDK, unlike `useCourseCustomFieldService`.
 * The service is reached through the `useACCounsellingServices()` aggregate, which this app already
 * uses elsewhere. It is destructured into a local so the adapter's `useMemo` keys on a stable identity.
 */
import { useMemo } from 'react';
import { Center, Loader } from '@mantine/core';
import { useACCounsellingServices } from '@actschurch/ccms-sdk';

import {
  FormBuilder,
  createCustomFieldAdapter,
  useFieldTypeCatalogue,
} from '@/components/advanced/FormBuilder';

const TITLE = 'Help Request Custom Form';

// ---------------------------------------------------------------------------
// Editable
// ---------------------------------------------------------------------------

export interface NeedFormPageProps {
  needID: number;
  /** Called when the builder's Done button is pressed. Usually closes the hosting modal. */
  onDone?: () => void;
}

export function NeedFormPage({ needID, onDone }: NeedFormPageProps) {
  const { needCustomField } = useACCounsellingServices();
  const fieldTypes = useFieldTypeCatalogue();

  const adapter = useMemo(() => {
    if (!fieldTypes) return null;
    return createCustomFieldAdapter({
      fieldTypes,
      list: () => needCustomField.byNeed(needID),
      create: (data) => needCustomField.create({ ...data, needID }),
      update: (id, data) => needCustomField.update(id, data),
      remove: (id) => needCustomField.delete(id),
    });
  }, [fieldTypes, needID, needCustomField]);

  if (!adapter || !fieldTypes) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <FormBuilder
      title={TITLE}
      subtitle="Asked on the public help-request form whenever someone picks this need"
      adapter={adapter}
      formTypes={fieldTypes}
      onDone={onDone}
    />
  );
}

// ---------------------------------------------------------------------------
// Read-only — same component and adapter shape, just `readonly`, so there is no
// palette, no edit/delete/move controls and no Done button. Only `list` is needed,
// since nothing here ever writes.
// ---------------------------------------------------------------------------

export interface NeedFormViewerProps {
  needID: number;
}

export function NeedFormViewer({ needID }: NeedFormViewerProps) {
  const { needCustomField } = useACCounsellingServices();
  const fieldTypes = useFieldTypeCatalogue();

  const adapter = useMemo(() => {
    if (!fieldTypes) return null;
    return createCustomFieldAdapter({
      fieldTypes,
      list: () => needCustomField.byNeed(needID),
    });
  }, [fieldTypes, needID, needCustomField]);

  if (!adapter || !fieldTypes) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <FormBuilder
      title={TITLE}
      subtitle="Read-only view"
      adapter={adapter}
      formTypes={fieldTypes}
      readonly
    />
  );
}

export default NeedFormPage;
